import { State } from '@progress/kendo-data-query';
import { useTableKeyboardNavigation } from '@progress/kendo-react-data-tools';
import { CalendarProps, CalendarPropsContext } from '@progress/kendo-react-dateinputs';
import {
  GridColumn as Column,
  GRID_COL_INDEX_ATTRIBUTE,
  Grid,
  GridCellProps,
  GridColumnMenuFilter,
  GridColumnMenuWrapper,
  GridColumnProps,
  GridColumnResizeEvent,
  GridDataStateChangeEvent,
  GridDetailRowProps,
  GridExpandChangeEvent,
  GridFilterOperators,
  GridHeaderCellProps,
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridNoRecords,
  GridPagerSettings,
  GridRowClickEvent,
  GridRowDoubleClickEvent,
  GridSelectionChangeEvent,
  GridSortChangeEvent,
  GridToolbar,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import {
  LocalizationProvider,
  loadMessages,
  useInternationalization,
} from '@progress/kendo-react-intl';
import React, {
  CSSProperties,
  ComponentType,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import EditCellContextProvider from 'src/contexts/EditCellContext';
import GridContextProvider from 'src/contexts/GridContext';
import useAuth from 'src/hooks/useAuth';
import 'src/style/grid/grid-override.scss';
import { round } from 'src/utils/number';
import LoadingPanel from './LoadingPanel';
import CustomFilterCell from './custom-cells/CustomFilterCell';

const cellStyles = {
  string: 'left-justify',
  number: 'left-justify',
  date: 'left-justify',
  dollar: 'right-justify',
  fixed: 'center-justify',
  icon: 'center-justify',
  weight: 'right-justify',
};

type ScrollMode = 'none' | 'scrollable' | 'virtual';
const SELECTED_FIELD: string = 'selected';

const generalTable = (
  param: string,
  t: (key: string, options?: Record<string, string>) => string
) => ({
  grid: {
    pagerItemsPerPage: t('pagerItemsPerPage', { items: t(param) }),
    pagerInfo: t('pagerInfo', { items: t(param) }),
    isNullOrEmpty: t('Is null or empty'),
    notIsNullOrEmpty: t('Is not null or empty'),
  },
});

export interface GridColumn<D> extends GridColumnProps {
  component?: ComponentType<GridCellProps>;
  menu?: boolean;
  dataType?: 'string' | 'number' | 'date' | 'dollar' | 'fixed' | 'icon' | 'weight';
  specificStyle?: (field: string, value: D) => CSSProperties;
  headerCellTooltip?: string;
  childrenColumn?: Omit<GridColumn<D>, 'childrenColumn'>[];
  customFilter?: boolean;
  step?: number;
  filterFormat?: string;
  headerCell?: React.ComponentType<GridHeaderCellProps>;
  show?: boolean;
}

interface GridProps<D> {
  id?: string;
  columns: GridColumn<D>[];
  sortable?: boolean;
  resizable?: boolean;
  total?: number;
  dataState?: State;
  dataSource: D[];
  onDataStateChange?: (e: GridDataStateChangeEvent) => void;
  onItemChange?: (e: GridItemChangeEvent) => void;
  height?: string;
  cellRender?: any;
  rowRender?: any;
  DataItemKey: string;
  selectable?: GridSelectableSettings;
  onSelectionChange?: (event: GridSelectionChangeEvent) => void;
  onRowClick?: (event: GridRowClickEvent) => void;
  onHeaderSelectionChange?: (event: GridHeaderSelectionChangeEvent) => void;
  onSortChange?: (e: GridSortChangeEvent) => void;
  gridToolbarChildren?: React.ReactNode;
  headerCellRender?: (
    defaultRendering: React.ReactNode,
    props: GridHeaderCellProps
  ) => React.ReactNode;
  showColumnMenu?: boolean;
  additionalCellFunctions?: any[];
  cellClick?: (e: any) => any;
  pageable?: boolean | GridPagerSettings;
  scrollable?: ScrollMode;
  loading?: boolean;
  fieldModifiable?: boolean;
  tableTextId?: string;
  className?: string;
  detail?: ComponentType<GridDetailRowProps>;
  headerClassName?: string;
  rowHeight?: number;
  onExpandChange?: (e: GridExpandChangeEvent) => void;
  onRowDoubleClick?: (e: GridRowDoubleClickEvent) => void;
  style?: CSSProperties;
  minHeight?: string;
  isCustomExpandableRow?: boolean;
  noRecordText?: string;
  onColumnResize?: (event: GridColumnResizeEvent) => void;
  disableAutoResize?: boolean;
  hideScrollBar?: boolean;
  disableCellTooltip?: boolean;
}

const GridCellWithToolTip =
  <D,>(column?: GridColumn<D>, disableToolTip?: boolean) =>
  (props: GridCellProps) => {
    let defaultRendering: React.ReactElement<HTMLTableCellElement> | null = null;
    const field = props.field || '';
    const value = props?.dataItem?.[field];
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const navigationAttributes = useTableKeyboardNavigation(props.id);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const intl = useInternationalization();
    let dataAsString = '';
    if (value !== undefined && value !== null) {
      dataAsString = props.format ? intl.format(props.format, value) : value.toString();
    }

    // Build tooltip props conditionally
    const tooltipProps = disableToolTip
      ? {}
      : {
          'data-tooltip-id': 'global-tooltip',
          'data-tooltip-content': dataAsString,
        };

    defaultRendering = (
      <td
        style={column?.specificStyle?.(field, props.dataItem)}
        className={cellStyles[column?.dataType || 'string'] || ''}
        colSpan={props.colSpan}
        role={'gridcell'}
        aria-colindex={props.ariaColumnIndex}
        aria-selected={props.isSelected}
        {...tooltipProps}
        {...{
          [GRID_COL_INDEX_ATTRIBUTE]: props.columnIndex,
        }}
        {...navigationAttributes}
      >
        {dataAsString}
      </td>
    ) as React.ReactElement<HTMLTableCellElement>;

    return props.render ? props.render.call(undefined, defaultRendering, props) : defaultRendering;
  };

const filterOperators: GridFilterOperators = {
  text: [
    { text: 'grid.filterContainsOperator', operator: 'contains' },
    { text: 'grid.filterNotContainsOperator', operator: 'doesnotcontain' },
    { text: 'grid.filterEqOperator', operator: 'eq' },
    { text: 'grid.filterNotEqOperator', operator: 'neq' },
    { text: 'grid.filterStartsWithOperator', operator: 'startswith' },
    { text: 'grid.filterEndsWithOperator', operator: 'endswith' },
    { text: 'grid.isNullOrEmpty', operator: 'isnull' },
    { text: 'grid.notIsNullOrEmpty', operator: 'isnotnull' },
  ],
  numeric: [
    { text: 'grid.filterEqOperator', operator: 'eq' },
    { text: 'grid.filterNotEqOperator', operator: 'neq' },
    { text: 'grid.filterGteOperator', operator: 'gte' },
    { text: 'grid.filterGtOperator', operator: 'gt' },
    { text: 'grid.filterLteOperator', operator: 'lte' },
    { text: 'grid.filterLtOperator', operator: 'lt' },
    { text: 'grid.filterIsNullOperator', operator: 'isnull' },
    { text: 'grid.filterIsNotNullOperator', operator: 'isnotnull' },
  ],
  date: [
    { text: 'grid.filterEqOperator', operator: 'eq' },
    { text: 'grid.filterNotEqOperator', operator: 'neq' },
    { text: 'grid.filterAfterOrEqualOperator', operator: 'gte' },
    { text: 'grid.filterAfterOperator', operator: 'gt' },
    { text: 'grid.filterBeforeOperator', operator: 'lt' },
    { text: 'grid.filterBeforeOrEqualOperator', operator: 'lte' },
    { text: 'grid.filterIsNullOperator', operator: 'isnull' },
    { text: 'grid.filterIsNotNullOperator', operator: 'isnotnull' },
  ],
  boolean: [{ text: 'grid.filterEqOperator', operator: 'eq' }],
};

function findColumnByField<D>(field: string, columns: GridColumn<D>[]): GridColumn<D> | undefined {
  for (const column of columns) {
    if (column.field === field) {
      return column;
    } else if (column.childrenColumn?.length) {
      let subColumn = findColumnByField(field, column.childrenColumn);
      if (subColumn) {
        return subColumn;
      }
    }
  }
}
const widthChecker = (width: number | string) =>
  typeof width === 'string' ? +width.replace('px', '') : width;

const GridWrapper = <D extends { selected: boolean }>(
  {
    id,
    columns: initialColumns,
    sortable,
    resizable,
    total,
    dataState,
    dataSource,
    onDataStateChange,
    onItemChange,
    height,
    cellRender,
    rowRender,
    showColumnMenu = false,
    DataItemKey,
    selectable,
    onSelectionChange,
    onRowClick,
    onHeaderSelectionChange,
    gridToolbarChildren,
    cellClick,
    pageable,
    scrollable,
    loading = false,
    fieldModifiable = false,
    tableTextId = 'rows',
    className = '',
    detail,
    headerClassName,
    rowHeight,
    onExpandChange,
    onRowDoubleClick,
    headerCellRender,
    style,
    minHeight,
    isCustomExpandableRow,
    noRecordText = 'No records available',
    onColumnResize,
    disableAutoResize = false,
    hideScrollBar,
    disableCellTooltip = false,
  }: GridProps<D>,
  ref: React.ForwardedRef<Grid>
) => {
  const { t, i18n } = useTranslation();
  const [columns, setColumns] = useState(initialColumns);
  const { isShuster } = useAuth();
  const isAllRowsSelected = useMemo(
    () => (dataSource?.length > 0 ? dataSource?.every((row) => row?.selected) : false),
    [dataSource]
  );
  const handleExpandChange = useCallback(
    (e: GridExpandChangeEvent) => {
      onItemChange?.({ ...e, field: 'expanded' });
    },
    [onItemChange]
  );
  const randomId = useId();
  const tableId = useMemo(() => id || randomId, [id, randomId]);

  const handleResize = useCallback(() => {
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    setTimeout(() => {
      const tableProps = document.getElementById(tableId);

      const tableUIWidth = tableProps?.clientWidth || 0;
      const tableWithExplicitExpaned = initialColumns.filter((col) => col.field === 'expanded');
      const tableWidth =
        (detail && tableWithExplicitExpaned.length === 0 ? tableUIWidth - 32 : tableUIWidth) -
        (isChrome && hideScrollBar ? 17 : 0);

      const columnsWidth = initialColumns
        .map((col) => {
          if (col.childrenColumn) {
            return col.childrenColumn.map((child) => {
              if (child.show ?? true) return +widthChecker(child.width as any);
              else return 0;
            });
          } else if (col.width && (col.show ?? true)) {
            return +widthChecker(col.width);
          } else return [];
        })
        .flat()
        .reduce((ac, co) => (ac || 0) + (co || 0), 0);

      const fixedWidthFields = ['selected', 'errors', 'expanded', 'file', 'dataIndex'];
      const columnsWithFixedWidth = initialColumns.filter((col) =>
        fixedWidthFields.includes(col.field as string)
      );
      if (round(columnsWidth) < round(tableWidth)) {
        const isShowAdded = initialColumns.every((col) => col.show !== undefined);
        const visibleColumns = isShowAdded
          ? initialColumns.filter((co) => co.show)
          : initialColumns;
        const totalColumns = visibleColumns
          .map((col) => {
            if (col.childrenColumn)
              return col.childrenColumn.filter((child) => child.show ?? child);
            else return col;
          })
          .flat();
        const adjustedColumns = visibleColumns.map((col) => {
          if (col.childrenColumn)
            return {
              ...col,
              childrenColumn: col.childrenColumn.map((child) => ({
                ...child,
                width: parseInt(
                  ((tableWidth - columnsWithFixedWidth.length * 40) /
                    (totalColumns.length - columnsWithFixedWidth.length)) as any
                ),
              })),
            };
          else if (fixedWidthFields.includes(col.field as string)) {
            return {
              ...col,
              width: 40,
            };
          } else
            return {
              ...col,
              width: parseInt(
                ((tableWidth - columnsWithFixedWidth.length * 40) /
                  (totalColumns.length - columnsWithFixedWidth.length)) as any
              ),
            };
        }) as any;
        const sortedColumnsWidth = adjustedColumns
          .map((col: GridColumn<D>) => {
            if (col.childrenColumn) {
              return col.childrenColumn.map((child) => {
                if (child.show ?? true) return +widthChecker(child.width as any);
                else return 0;
              });
            } else if (col.width && (col.show ?? true)) {
              return +widthChecker(col.width);
            } else return [];
          })
          .flat()
          .reduce((ac: number, co: number) => (ac || 0) + (co || 0), 0);
        const difference = tableWidth - sortedColumnsWidth;

        if (difference > 0) adjustedColumns[adjustedColumns.length - 1].width += difference;

        setColumns(adjustedColumns);
      } else setColumns(initialColumns);
    }, 15);
  }, [detail, hideScrollBar, initialColumns, tableId]);

  useEffect(() => {
    if (!disableAutoResize) {
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [disableAutoResize, handleResize]);

  const headerCell = useCallback(
    (props: GridHeaderCellProps) => {
      const column = findColumnByField(props.field || '', columns);
      const CustomHeaderCell = column?.headerCell;
      const headerTooltip = column?.headerCellTooltip || props.title;
      if (CustomHeaderCell) {
        return (
          <span className="k-cell-inner">
            <span
              className="k-link"
              onClick={props.onClick}
              data-tooltip-id={'global-tooltip'}
              data-tooltip-content={headerTooltip}
            >
              <CustomHeaderCell {...props} />
            </span>
            {props.children}
            {column?.menu !== false && <GridColumnMenuWrapper {...props.columnMenuWrapperProps} />}
          </span>
        );
      } else {
        return (
          <span className="k-cell-inner">
            <span className="k-link" onClick={props.onClick}>
              <span
                className="k-column-title"
                data-tooltip-id={'global-tooltip'}
                data-tooltip-content={headerTooltip}
              >
                {props.title}
              </span>
              {props.children}
            </span>
            {column?.menu !== false && <GridColumnMenuWrapper {...props.columnMenuWrapperProps} />}
          </span>
        );
      }
    },
    [columns]
  );

  const isColumnActive = useCallback(
    (field: string) => GridColumnMenuFilter.active(field, dataState?.filter),
    [dataState?.filter]
  );

  const generateHeaderClasses = useCallback(
    (field: string, sortable: boolean) =>
      `${isColumnActive(field) ? 'active' : ''} ${
        sortable === false ? 'no-sort' : ''
      } ${headerClassName}`,
    [headerClassName, isColumnActive]
  );

  const calendarProps = useCallback(
    (calendarProps: CalendarProps) => ({
      ...calendarProps,
      navigation: false,
    }),
    []
  );

  const localizationLanguage = `${tableTextId}-${i18n.language}`;
  useEffect(() => {
    if (tableTextId) {
      loadMessages(generalTable(tableTextId, t), localizationLanguage);
    }
  }, [tableTextId, i18n.language, localizationLanguage, t]);

  return (
    <>
      {loading && <LoadingPanel id={id} />}
      <LocalizationProvider language={localizationLanguage}>
        <CalendarPropsContext.Provider value={calendarProps}>
          <GridContextProvider
            value={{
              ref,
              data: dataSource,
            }}
          >
            <EditCellContextProvider value={[cellClick]}>
              <Grid
                id={tableId}
                className={`${className} ${isShuster ? 'shuster' : ''}`}
                onItemChange={onItemChange}
                pageable={pageable}
                sortable={sortable}
                resizable={resizable}
                total={total}
                style={{
                  height,
                  ...style,
                  minHeight,
                }}
                {...dataState}
                data={dataSource}
                dataItemKey={DataItemKey}
                selectedField={SELECTED_FIELD}
                selectable={selectable}
                cellRender={cellRender}
                rowRender={rowRender}
                onSelectionChange={onSelectionChange}
                onHeaderSelectionChange={onHeaderSelectionChange}
                onDataStateChange={onDataStateChange}
                scrollable={scrollable}
                editField={fieldModifiable ? 'inEdit' : undefined}
                ref={ref}
                expandField={detail ? 'expanded' : undefined}
                onExpandChange={
                  isCustomExpandableRow ? undefined : onExpandChange || handleExpandChange
                }
                detail={detail}
                onRowClick={onRowClick}
                rowHeight={rowHeight}
                filterOperators={filterOperators}
                onRowDoubleClick={onRowDoubleClick}
                headerCellRender={headerCellRender}
                onColumnResize={onColumnResize}
              >
                <GridNoRecords>{loading ? <span /> : noRecordText}</GridNoRecords>
                {gridToolbarChildren && <GridToolbar children={gridToolbarChildren} />}
                {columns.map((column) => {
                  if (column.field === 'selected') {
                    return (
                      <Column
                        key={column.field}
                        field={column.field}
                        title={column.title}
                        locked={column?.locked}
                        className={`${column?.className} k-grid-edit-cell k-table-td !text-center`}
                        headerSelectionValue={isAllRowsSelected}
                        headerClassName={`${generateHeaderClasses(
                          column.field as string,
                          column.sortable as boolean
                        )} !text-center`}
                        sortable={column?.sortable}
                        minResizableWidth={column?.minResizableWidth}
                        width={column.width}
                      />
                    );
                  }

                  if (column.childrenColumn?.length && column.childrenColumn) {
                    return (
                      (typeof column.show === 'undefined' || column.show) && (
                        <Column
                          key={column.field}
                          field={column.field}
                          title={column.title}
                          headerCell={headerCell}
                          filter={column?.filter}
                          locked={column.locked}
                          sortable={column?.sortable}
                          minResizableWidth={column?.minResizableWidth}
                          headerClassName={`${generateHeaderClasses(
                            column.field as string,
                            column.sortable as boolean
                          )} ${column.headerClassName}`}
                          className={column?.className}
                          cell={
                            column.component
                              ? column.component
                              : GridCellWithToolTip(column, disableCellTooltip)
                          }
                          width={column.width}
                        >
                          {column.childrenColumn
                            .filter((c) => typeof c.show === 'undefined' || c.show)
                            .map((item) => (
                              <Column
                                key={item.field}
                                columnMenu={(columnMenuProps) => {
                                  if (showColumnMenu) {
                                    if (item.columnMenu) {
                                      return <item.columnMenu {...columnMenuProps} />;
                                    } else {
                                      return (
                                        <CustomFilterCell
                                          {...columnMenuProps}
                                          step={item.step}
                                          filterFormat={item.filterFormat}
                                        />
                                      );
                                    }
                                  }
                                  return null;
                                }}
                                cell={
                                  item.component
                                    ? item.component
                                    : GridCellWithToolTip(column, disableCellTooltip)
                                }
                                locked={column.locked}
                                field={item.field}
                                title={item.title}
                                filter={item.filter}
                                width={item.width}
                                format={item.format}
                                className={item?.className}
                                headerCell={headerCell}
                                footerCell={item.footerCell}
                                headerClassName={`${generateHeaderClasses(
                                  item.field as string,
                                  item.sortable as boolean
                                )} ${item.headerClassName}`}
                                sortable={item?.sortable}
                                minResizableWidth={item?.minResizableWidth}
                              />
                            ))}
                        </Column>
                      )
                    );
                  }

                  return (
                    (typeof column.show === 'undefined' || column.show) && (
                      <Column
                        key={column.field}
                        columnMenu={(columnMenuProps) => {
                          if (showColumnMenu) {
                            if (column.columnMenu) {
                              return <column.columnMenu {...columnMenuProps} />;
                            } else {
                              return (
                                <CustomFilterCell
                                  {...columnMenuProps}
                                  step={column.step}
                                  filterFormat={column.filterFormat}
                                />
                              );
                            }
                          }
                          return null;
                        }}
                        cell={
                          column.component
                            ? column.component
                            : GridCellWithToolTip(column, disableCellTooltip)
                        }
                        field={column.field}
                        title={column.title}
                        filter={column.filter}
                        width={column.width}
                        locked={column?.locked}
                        format={column.format}
                        className={column?.className}
                        headerCell={headerCell}
                        footerCell={column.footerCell}
                        headerClassName={`${generateHeaderClasses(
                          column.field as string,
                          column.sortable as boolean
                        )} ${column.headerClassName}`}
                        sortable={column?.sortable}
                        minResizableWidth={column?.minResizableWidth}
                      />
                    )
                  );
                })}
              </Grid>
            </EditCellContextProvider>
          </GridContextProvider>
        </CalendarPropsContext.Provider>
      </LocalizationProvider>
    </>
  );
};

export default forwardRef(GridWrapper) as <T>(
  props: GridProps<T> & { ref?: React.ForwardedRef<Grid> }
) => ReturnType<typeof GridWrapper>;
